import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Cast helper for tables not yet in generated types
const fromAny = (table: string) => (supabase as unknown as { from: (t: string) => ReturnType<typeof supabase.from> }).from(table);

export interface ProductMappingFile {
  id: string;
  organisation_id: string;
  filename: string;
  active_identifier_at_upload: string;
  column_mappings: Array<{ column_header: string; identifier_type: string }>;
  row_count: number;
  created_at: string;
  created_by_email: string | null;
  deactivated_at: string | null;
  deactivated_by_email: string | null;
}

export interface ProductMapping {
  id: string;
  file_id: string;
  primary_type: string;
  primary_value: string;
  alias_type: string;
  alias_value: string;
  organisation_id: string;
  created_at: string;
  effective_from: string;
  effective_to: string | null;
  modified_by_email: string | null;
}

export interface ProductMetadata {
  id: string;
  organisation_id: string;
  file_id: string;
  primary_type: string;
  primary_value: string;
  attribute_name: string;
  attribute_value: string;
  created_at: string;
}

export function useProductMappingFiles(organisationId: string | undefined) {
  return useQuery({
    queryKey: ["product-mapping-files", organisationId],
    queryFn: async () => {
      const { data, error } = await fromAny("product_mapping_files")
        .select("*")
        .eq("organisation_id", organisationId!)
        .is("deactivated_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as ProductMappingFile[];
    },
    enabled: !!organisationId,
  });
}

export function useProductMappings(fileId: string | undefined) {
  return useQuery({
    queryKey: ["product-mappings", fileId],
    queryFn: async () => {
      const { data, error } = await fromAny("product_mappings")
        .select("*")
        .eq("file_id", fileId!)
        .is("effective_to", null)
        .order("primary_value");
      if (error) throw error;
      return data as unknown as ProductMapping[];
    },
    enabled: !!fileId,
  });
}

export function useAllProductMappings(organisationId: string | undefined) {
  return useQuery({
    queryKey: ["all-product-mappings", organisationId],
    queryFn: async () => {
      const { data, error } = await fromAny("product_mappings")
        .select("id, file_id, primary_type, primary_value, alias_type, alias_value, created_at, effective_from, effective_to, modified_by_email")
        .eq("organisation_id", organisationId!)
        .is("effective_to", null)
        .order("primary_value");
      if (error) throw error;
      return data as unknown as ProductMapping[];
    },
    enabled: !!organisationId,
  });
}

export function useProductMappingHistory(
  organisationId: string | undefined,
  aliasType: string | undefined,
  aliasValue: string | undefined
) {
  return useQuery({
    queryKey: ["product-mapping-history", organisationId, aliasType, aliasValue],
    queryFn: async () => {
      const { data, error } = await fromAny("product_mappings")
        .select("*")
        .eq("organisation_id", organisationId!)
        .eq("alias_type", aliasType!)
        .eq("alias_value", aliasValue!)
        .order("effective_from", { ascending: false });
      if (error) throw error;
      return data as unknown as ProductMapping[];
    },
    enabled: !!organisationId && !!aliasType && !!aliasValue,
  });
}

export function useProductMetadata(organisationId: string | undefined) {
  return useQuery({
    queryKey: ["product-metadata", organisationId],
    queryFn: async () => {
      const { data, error } = await fromAny("product_metadata")
        .select("*")
        .eq("organisation_id", organisationId!)
        .order("primary_value");
      if (error) throw error;
      return data as unknown as ProductMetadata[];
    },
    enabled: !!organisationId,
  });
}

export function useUpdateProductMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      existingId: string;
      organisationId: string;
      fileId: string;
      primary_type: string;
      primary_value: string;
      alias_type: string;
      alias_value: string;
      userEmail: string | null;
    }) => {
      const now = new Date().toISOString();

      const { error: expireErr } = await fromAny("product_mappings")
        .update({ effective_to: now, modified_by_email: input.userEmail })
        .eq("id", input.existingId)
        .is("effective_to", null);
      if (expireErr) throw expireErr;

      const { error: insertErr } = await fromAny("product_mappings").insert({
        organisation_id: input.organisationId,
        file_id: input.fileId,
        primary_type: input.primary_type,
        primary_value: input.primary_value,
        alias_type: input.alias_type,
        alias_value: input.alias_value,
        effective_from: now,
        created_by_email: input.userEmail,
        modified_by_email: input.userEmail,
      });
      if (insertErr) throw insertErr;
    },
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["all-product-mappings", v.organisationId] });
      queryClient.invalidateQueries({ queryKey: ["product-mapping-history"] });
    },
  });
}

export function useSoftDeleteProductMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      mappingId: string;
      organisationId: string;
      userEmail: string | null;
    }) => {
      const { error } = await fromAny("product_mappings")
        .update({ effective_to: new Date().toISOString(), modified_by_email: input.userEmail })
        .eq("id", input.mappingId)
        .is("effective_to", null);
      if (error) throw error;
    },
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["all-product-mappings", v.organisationId] });
      queryClient.invalidateQueries({ queryKey: ["product-mapping-history"] });
    },
  });
}

export function useDeactivateProductMappingFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      fileId: string;
      organisationId: string;
      userEmail: string | null;
    }) => {
      const now = new Date().toISOString();

      const { error: mapErr } = await fromAny("product_mappings")
        .update({ effective_to: now, modified_by_email: input.userEmail })
        .eq("file_id", input.fileId)
        .is("effective_to", null);
      if (mapErr) throw mapErr;

      const { error: fileErr } = await fromAny("product_mapping_files")
        .update({ deactivated_at: now, deactivated_by_email: input.userEmail })
        .eq("id", input.fileId);
      if (fileErr) throw fileErr;
    },
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["product-mapping-files", v.organisationId] });
      queryClient.invalidateQueries({ queryKey: ["product-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["all-product-mappings", v.organisationId] });
      queryClient.invalidateQueries({ queryKey: ["product-metadata"] });
      queryClient.invalidateQueries({ queryKey: ["product-mapping-history"] });
    },
  });
}

// Backward-compat alias — callers that passed a bare fileId string will need updating
export const useDeleteProductMappingFile = useDeactivateProductMappingFile;
